import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { keyBy } from 'lodash';

import Button from 'components/shared/Button';

import SpecialContentMenu, { SpecialContentMenuProps } from './SpecialContentMenu';

import type { WosIconProp } from 'types/common_types';

export type SidebarPrimaryGroup = {
  id: string;
  triggerIcon: WosIconProp;
  disabled: boolean;
  menuProps: Pick<SpecialContentMenuProps, 'title' | 'items' | 'handleDone' | 'handleViewManage'>;
  index: number;
  onOpen: () => void;
};

type SidebarGroup = {
  id: string;
  primaryGroup: SidebarPrimaryGroup[];
  secondaryGroup?: React.ReactNode;
};

export type SpecialContentSidebarProps = {
  topMenuGroup: SidebarGroup[];
  bottomMenuItems: { id: string; el: React.ReactNode }[];
  activeMenu: string | null;
  setActiveMenu: (id: string | null) => void;
  containerPadding?: number;
};

let sidebarAnimationStart: number | null;
let openAnimationFrame: number;
let closeAnimationFrame: number;

function SpecialContentSidebar({
  topMenuGroup,
  bottomMenuItems,
  activeMenu: propActiveMenu,
  setActiveMenu: setPropActiveMenu,
  containerPadding = 0,
}: SpecialContentSidebarProps) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuAnimatingIn, setMenuAnimatingIn] = useState(false);

  const specialContentMenusById = useMemo(() => {
    const primaryGroups = topMenuGroup.flatMap((group) => group.primaryGroup);

    return keyBy([...primaryGroups, ...bottomMenuItems], 'id');
  }, [topMenuGroup, bottomMenuItems]);

  const activeSpecialContentMenu = useMemo(() => {
    if (!activeMenu) return null;

    const activeGroupIndex = topMenuGroup.findIndex((group) => {
      return group.primaryGroup.some((item) => item.id === activeMenu);
    });

    const previousGroupIds = topMenuGroup
      .slice(0, activeGroupIndex)
      .map(({ id }) => `content-menu-group-${id}`);

    const item = specialContentMenusById[activeMenu] as SidebarPrimaryGroup;

    return (
      <SpecialContentMenu
        {...item.menuProps}
        previousItemIds={previousGroupIds}
        itemIndex={item.index}
        handleCloseMenu={handleCloseMenu}
        containerPadding={containerPadding}
      />
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu, specialContentMenusById, topMenuGroup, containerPadding]);

  const manageSidebarOpenAnimation = useCallback((timestamp: number) => {
    if (!sidebarAnimationStart) {
      sidebarAnimationStart = timestamp;
      openAnimationFrame = window.requestAnimationFrame(manageSidebarOpenAnimation);
    } else {
      setMenuAnimatingIn(true);
      sidebarAnimationStart = null;
    }
  }, []);

  const handleOpenMenu = useCallback(
    (id: string) => {
      setActiveMenu(id);

      if (propActiveMenu !== id) {
        setPropActiveMenu(id);
      }

      openAnimationFrame = window.requestAnimationFrame(manageSidebarOpenAnimation);
    },
    [manageSidebarOpenAnimation, propActiveMenu, setPropActiveMenu]
  );

  function manageSidebarCloseAnimation(timestamp: number) {
    if (!sidebarAnimationStart) {
      sidebarAnimationStart = timestamp;
    }

    const elapsed = timestamp - sidebarAnimationStart;

    // Slightly less than the CSS animation
    if (elapsed < 150) {
      closeAnimationFrame = window.requestAnimationFrame(manageSidebarCloseAnimation);
    } else {
      setActiveMenu(null);
      sidebarAnimationStart = null;
    }
  }

  function handleCloseMenu() {
    setMenuAnimatingIn(false);
    setPropActiveMenu(null);
    closeAnimationFrame = window.requestAnimationFrame(manageSidebarCloseAnimation);
  }

  function buildSidebarGroup(group: SidebarGroup) {
    const { primaryGroup, secondaryGroup, id } = group;
    const primaryItems = primaryGroup.map((primaryItem) => {
      const active = primaryItem.id === activeMenu;
      return (
        <Button
          key={primaryItem.id}
          type="icon"
          size="xl"
          icon={primaryItem.triggerIcon}
          id={`content-menu-trigger-${primaryItem.id}`}
          disabled={primaryItem.disabled}
          a11yText={primaryItem.menuProps.title}
          className={`special-content-menu-trigger ${active ? 'active' : ''}`}
          onClick={() => handleToggleMenu(primaryItem)}
          tooltipProps={{ placement: 'left' }}
        />
      );
    });

    return (
      <li key={id} id={`content-menu-group-${id}`}>
        {primaryItems}
        {secondaryGroup && secondaryGroup}
      </li>
    );
  }

  function handleToggleMenu(item: SidebarPrimaryGroup) {
    const { id, onOpen } = item;

    if (activeMenu === id) {
      handleCloseMenu();
    } else {
      onOpen();
      handleOpenMenu(id);
    }
  }

  useEffect(() => {
    function handleOffMenuClick(e: MouseEvent) {
      const sidebar = document.querySelector('.special-content-sidebar');
      const menu = document.querySelector('.special-content-menu');
      const clickedEl = e.target as HTMLElement;

      const shouldCloseMenu =
        sidebar && menu && !sidebar.contains(clickedEl) && !menu.contains(clickedEl);

      if (shouldCloseMenu) {
        handleCloseMenu();
      }
    }

    document.addEventListener('click', handleOffMenuClick);

    return function cleanup() {
      document.removeEventListener('click', handleOffMenuClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (propActiveMenu && propActiveMenu !== activeMenu) {
      handleOpenMenu(propActiveMenu);
    }
  }, [activeMenu, propActiveMenu, handleOpenMenu]);

  // Cancel the animation handling if component is unmounted
  useEffect(() => {
    return function cleanup() {
      window.cancelAnimationFrame(closeAnimationFrame);
      window.cancelAnimationFrame(openAnimationFrame);
    };
  }, []);

  return (
    <div
      className={`special-content-sidebar ${menuAnimatingIn ? 'in' : ''}`}
      style={{ paddingTop: containerPadding }}
    >
      <ul className="top-items">{topMenuGroup.map(buildSidebarGroup)}</ul>
      <ul className="bottom-items">
        {bottomMenuItems.map((item) => (
          <li key={item.id}>{item.el}</li>
        ))}
      </ul>
      {activeMenu && activeSpecialContentMenu}
    </div>
  );
}

export default SpecialContentSidebar;
